import { createTheme } from "@mui/material";
import { blue, green, red, grey, amber, purple, yellow } from "@mui/material/colors";
import { esES as dataGridEsES } from "@mui/x-data-grid";
import { esES as coreEsES } from "@mui/material/locale";
import { esES } from "@mui/x-date-pickers/locales";

// https://zenoo.github.io/mui-theme-creator/
// https://mui.com/material-ui/customization/default-theme/

const YELLOW = {
	main: "#F0C400",
	hover: "#CCA700",
	press: "#997D00",
};

const SHADES = {
	100: "#E7E6E4",
	200: "#CECDCA",
	400: "#9D9A95",
	600: "#504E49",
	700: "#474541",
};

const BASE_DARK = "#190007";

const RabbitLight = createTheme(
	{
		palette: {
			mode: "light",
			background: {
				default: "#EBEAE0",
				paper: "#EBEAE0",
				close: "#e0e0d7",
			},
			primary: {
				main: "#9D11F2",
			},
			secondary: {
				main: "#C85DF0",
			},
			success: {
				main: "#00EA84",
			},
			warning: {
				main: "#F0C300",
			},
			error: {
				main: "#FE0037",
			},
			info: {
				main: "#2F43FF",
			},
		},
	},
	esES,
	dataGridEsES,
	coreEsES,
);

const RabbitDark = createTheme(
	{
		palette: {
			mode: "dark",
			background: {
				default: "#190007",
				paper: "#190007",
				close: "#362129",
			},
			primary: {
				main: "#9D11F2",
			},
			secondary: {
				main: "#C85DF0",
			},
			success: {
				main: "#00EA84",
			},
			warning: {
				main: "#F0C300",
			},
			error: {
				main: "#FE0037",
			},
			info: {
				main: "#2F43FF",
			},
		},
	},
	esES,
	dataGridEsES,
	coreEsES,
);

const LightTheme = createTheme(
	{
		palette: {
			mode: "light",
			background: {
				default: "#fff",
				paper: "#fff",
				close: grey[50],
			},
			primary: {
				main: YELLOW.main,
				light: purple[50],
				dark: BASE_DARK,
				contrastText: BASE_DARK,
			},
			secondary: {
				main: grey[300],
				sub: grey[100],
			},
			success: {
				main: green[500],
				contrastText: "#fff",
			},
			warning: {
				main: amber[500],
				contrastText: "#fff",
			},
			error: {
				main: red[500],
			},
			info: {
				main: blue[500],
			},
			divider: SHADES[200],
			text: {
				primary: BASE_DARK,
			},
		},

		typography: {
			fontFamily: `"Inter","Roboto","Helvetica","Arial",sans-serif`,
		},

		components: {
			MuiCard: {
				defaultProps: { elevation: 0 },
				styleOverrides: {
					root: ({ theme }) => ({
						boxShadow: "none",
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: 20,
					}),
				},
			},

			MuiPaper: {
				styleOverrides: {
					root: {
						boxShadow: "none",
					},
				},
			},

			MuiDialog: {
				styleOverrides: {
					paper: ({ theme }) => ({
						borderRadius: 20,
						border: `1px solid ${theme.palette.divider}`,
						boxShadow: "none",
					}),
				},
			},

			MuiOutlinedInput: {
				styleOverrides: {
					root: ({ theme }) => ({
						borderRadius: 16,
						"& .MuiOutlinedInput-notchedOutline": {
							borderColor: theme.palette.divider,
						},
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: theme.palette.text.primary,
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: theme.palette.primary.main,
							borderWidth: 2,
						},
					}),
				},
			},

			MuiCheckbox: {
				styleOverrides: {
					root: ({ theme }) => ({
						color: theme.palette.text.primary,
						"&.Mui-checked": {
							color: theme.palette.text.primary,
						},
						"&.MuiCheckbox-indeterminate": {
							color: theme.palette.text.primary,
						},
					}),
				},
			},

			MuiButton: {
				defaultProps: {
					disableElevation: true,
				},
				styleOverrides: {
					root: ({ ownerState }) => ({
						borderRadius: 9999,
						textTransform: "none",
						fontWeight: 600,
						...(ownerState.size === "small"
							? { height: 36, padding: "6px 16px" }
							: { height: 44, padding: "10px 20px" }),
					}),

					containedPrimary: {
						backgroundColor: YELLOW.main,
						color: BASE_DARK,
						border: "none",

						"&:hover": {
							backgroundColor: YELLOW.hover,
							border: "none",
						},
						"&:active": {
							backgroundColor: YELLOW.press,
						},

						"&.Mui-disabled": {
							backgroundColor: SHADES[200],
							color: SHADES[400],
						},

						"&[data-tone='dark']": {
							backgroundColor: BASE_DARK,
							color: "#fff",
							"&:hover": {
								backgroundColor: SHADES[600],
							},
							"&:active": {
								backgroundColor: SHADES[700],
							},
							"&.Mui-disabled": {
								backgroundColor: SHADES[200],
								color: SHADES[400],
							},
						},
					},

					outlined: {
						borderWidth: "1px",
						borderColor: BASE_DARK,
						color: BASE_DARK,
						backgroundColor: "transparent",

						"&:hover": {
							borderColor: BASE_DARK,
							backgroundColor: SHADES[100],
						},
						"&:active": {
							backgroundColor: SHADES[200],
						},

						"&.Mui-disabled": {
							borderColor: SHADES[200],
							color: SHADES[400],
						},
					},

					text: {
						color: BASE_DARK,
						backgroundColor: "transparent",
						"&:hover": {
							backgroundColor: "transparent",
							color: SHADES[600],
						},
						"&:active": {
							color: SHADES[700],
						},
						"&.Mui-disabled": {
							color: SHADES[400],
						},
					},
				},
			},

			MuiLoadingButton: {
				styleOverrides: {
					root: {
						"&.MuiLoadingButton-loading": {
							backgroundColor: SHADES[200],
							color: SHADES[400],
						},
						"& .MuiLoadingButton-loadingIndicator": {
							color: SHADES[400],
						},
					},
				},
			},

			MuiDrawer: {
				styleOverrides: {
					paper: ({ theme }) => ({
						backgroundColor: theme.palette.primary.dark,
						color: "#fff",
						borderRight: `1px solid ${theme.palette.primary.dark}`,
					}),
				},
			},

			MuiListItemIcon: {
				styleOverrides: {
					root: {
						color: "inherit",
					},
				},
			},
		},
	},
	esES,
	dataGridEsES,
	coreEsES,
);

const DarkTheme = createTheme(
	{
		palette: {
			mode: "dark",
			background: {
				default: "#000",
				paper: "#000",
				close: grey[900],
			},
			primary: {
				main: green[900],
			},
			secondary: {
				main: blue[900],
			},
			success: {
				main: green[900],
			},
			warning: {
				main: yellow[800],
			},
			error: {
				main: red[500],
			},
			info: {
				main: blue[900],
			},
		},
	},
	esES,
	dataGridEsES,
	coreEsES,
);

export { RabbitLight, RabbitDark, LightTheme, DarkTheme };
